import React from 'react'
import { useNewTranslation } from 'hooks'
import { DollarSign, Slash, Save, XCircle } from 'react-feather'
import { theme } from 'theme'
import { SUBMISSION_STATUS_ENUM } from 'state/submissions'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import { Payout } from 'components/Shared/Permission/enums'
import Restricted from 'components/Shared/Permission/Restricted'
import IconButton from 'components/Shared/Button/IconButton'
import styled, { css } from 'particles/themes/styled'
import Icon from 'components/Icon/Icon'
import ParticlesIcon from 'particles/Icon'
import PayoutStatusCell from './PayoutStatusCell'

const payIconColor = css`
    background-color: ${theme.colors.jungleGreen};
`
const editIconColor = css`
    background-color: ${theme.colors.orange};
`
const disqualifyIconColor = css`
    background-color: ${theme.colors.frenchRose};
`
const paidOutsideAppColor = css`
    background-color: ${({ theme }) => theme.colors.surface.positive};
`

const cancelIconColor = css`
    background-color: ${theme.colors.doveGrey};
`
const IconButtonContainer = styled.div`
    .icon-button {
        border: none;
    }
`

const VerticalDivider = styled.div`
    width: 1px;
    height: 16px;
    background: ${({ theme }) => theme.colors.border.primary};
`

const ButtonContainer = styled.div`
    display: flex;
    width: 100%;
    align-items: center;
    gap: 12px;
`

const PerformanceActionCell = (params: GridRenderCellParams) => {
    const [t] = useNewTranslation()
    const cellParams = params

    const { uuid } = cellParams.row
    const isEditing = cellParams.value.editingSubmissionPerformance
        ? uuid === cellParams.value.editingSubmissionPerformance.submissionUuid
        : false

    if (cellParams.value.paymentStatus === SUBMISSION_STATUS_ENUM.FINISHED && isEditing)
        return (
            <ButtonContainer>
                <IconButtonContainer>
                    <IconButton
                        type="button"
                        onClick={() => {
                            cellParams.value.savePerformance()
                        }}
                        isActive={!cellParams.value.isUpdateLoading}
                        buttonType="outlined"
                        size="xs"
                        customStyle={payIconColor}
                        tooltipText={t('button.save')}
                        className="icon-button"
                    >
                        <Save stroke={theme.newColors.neutral50} display={'block'} />
                    </IconButton>
                </IconButtonContainer>

                <IconButtonContainer>
                    <IconButton
                        type="button"
                        onClick={() => {
                            cellParams.value.cancelEditPerformance()
                        }}
                        isActive={!cellParams.value.isUpdateLoading}
                        buttonType="outlined"
                        size="xs"
                        customStyle={cancelIconColor}
                        tooltipText={t('button.cancel')}
                        className="icon-button"
                    >
                        <XCircle stroke={theme.newColors.neutral50} display={'block'} />
                    </IconButton>
                </IconButtonContainer>
            </ButtonContainer>
        )
    if (cellParams.value.paymentStatus === SUBMISSION_STATUS_ENUM.FINISHED && !isEditing)
        return (
            <ButtonContainer>
                <Restricted permissions={[Payout.PayPartiposters]}>
                    <IconButtonContainer>
                        <IconButton
                            type="button"
                            onClick={() => {
                                cellParams.value.payPartiposter(cellParams.row.uuid)
                            }}
                            buttonType="outlined"
                            size="xs"
                            customStyle={payIconColor}
                            tooltipText={t('button.pay')}
                            className="icon-button"
                            data-testid="pay-button"
                        >
                            <DollarSign stroke={theme.newColors.neutral50} display={'block'} />
                        </IconButton>
                    </IconButtonContainer>
                </Restricted>
                <Restricted permissions={[Payout.UpdatePayouts]}>
                    <IconButtonContainer>
                        <IconButton
                            type="button"
                            onClick={() => {
                                cellParams.value.editPerformance(cellParams.row.uuid)
                            }}
                            buttonType="outlined"
                            size="xs"
                            customStyle={editIconColor}
                            tooltipText={t('button.edit')}
                            className="icon-button"
                        >
                            <Icon.EditCircle style={{ stroke: theme.newColors.neutral50 }} />
                        </IconButton>
                    </IconButtonContainer>
                </Restricted>
                <Restricted permissions={[Payout.DisqualifyPartiposters]}>
                    <IconButtonContainer>
                        <IconButton
                            type="button"
                            onClick={() => {
                                cellParams.value.disqualifyPartiposter(cellParams.row.uuid)
                            }}
                            buttonType="outlined"
                            size="xs"
                            customStyle={disqualifyIconColor}
                            tooltipText={t('button.disqualify')}
                            className="icon-button"
                            data-testid="disqualify-button"
                        >
                            <Slash stroke={theme.newColors.neutral50} display={'block'} />
                        </IconButton>
                    </IconButtonContainer>
                </Restricted>
                <Restricted permissions={[Payout.PayPartiposters]}>
                    <VerticalDivider />
                    <IconButtonContainer>
                        <IconButton
                            type="button"
                            onClick={() => {
                                cellParams.value.paidOutsideApp(cellParams.row.uuid)
                            }}
                            buttonType="outlined"
                            size="xs"
                            customStyle={paidOutsideAppColor}
                            tooltipText={t('statusLabel.paidOutsideApp')}
                            className="icon-button"
                            data-testid="disqualify-button"
                            disableFocusStyle
                        >
                            <ParticlesIcon name="handDeposit" color="actionInverse" />
                        </IconButton>
                    </IconButtonContainer>
                </Restricted>
            </ButtonContainer>
        )

    return <PayoutStatusCell {...params} />
}

export default PerformanceActionCell
